import {
  ChatBubbleOutline,
  SendRounded,
} from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import isFunction from "lodash/isFunction";
import isNil from "lodash/isNil";
import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useChecklistRouteHook } from "../../hooks/useChecklistRouteHook";
import { useUnderwritingHook } from "../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../redux/actions/loanDrilldownActions";
import { getUserFullNameFromEmail } from "../../utils/functions/getUserFullNameFromEmail";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import HtmlEditor from "../html/HtmlEditor";
import Flx from "../layout/Flx";
import BasicModal from "../modals/BasicModal";
import Txt from "../typography/Txt";

// ── Palette (slate-leaning neutrals) ──────────────────────────────────────────
const palette = {
  border: "#e2e8f0",
  borderSubtle: "#eef2f7",
  background: "#f8fafc",
  surface: "#ffffff",
  text: "#0f172a",
  textMuted: "#475569",
  textFaint: "#94a3b8",
  accent: "#4f46e5",
  accentLight: "#eef2ff",
};

// ── Supported comment types ──────────────────────────────────────────────────
// "loan"        → posts a deal-level comment (createDealComment)
// "exception"   → posts a comment on an underwriting exception
// "requirement" → posts a comment on an underwriting requirement / condition
// "task"        → posts a comment on a checklist task
export const COMMENT_TYPES = {
  LOAN: "loan",
  EXCEPTION: "exception",
  REQUIREMENT: "requirement",
  TASK: "task",
};

const DEFAULT_TITLES = {
  loan: "Add Loan Comment",
  exception: "Add Exception Comment",
  requirement: "Add Condition Comment",
  task: "Add Task Comment",
};

/**
 * Reusable comment modal.
 *
 * Props
 *   open            — modal visibility
 *   onClose         — close handler
 *   commentType     — one of COMMENT_TYPES (loan / exception / requirement / task)
 *   context         — type-specific IDs, e.g.
 *                       loan        → { dealId }
 *                       exception   → { dealId, exceptionId }
 *                       requirement → { dealId, requirementId }
 *                       task        → { dealId, taskListId, taskId }
 *   comments        — optional array of existing comments to display under the editor
 *   title           — optional custom modal title (defaults to a type-appropriate title)
 *   placeholder     — optional editor placeholder
 *   onSuccess       — optional callback fired with the API response
 *   dispatchLoanDrilldownSet — defaults to true; set false if the response is not the loan object
 */
const CommentModal = ({
  open,
  onClose,
  commentType = COMMENT_TYPES.LOAN,
  context = {},
  comments,
  title,
  placeholder = "Write a comment…",
  onSuccess,
  dispatchLoanDrilldownSet = true,
}) => {
  const dispatch = useDispatch();
  const users = useSelector((state) => state.users);
  const { createDealComment } = useUnderwritingHook();
  const {
    addCommentToUnderwritingException,
    addCommentToUnderwritingRequirement,
    createChecklistComment,
    loading: checklistLoading,
  } = useChecklistRouteHook();

  const [editorValue, setEditorValue] = useState("");
  const [editorKey, setEditorKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const resolvedTitle = title || DEFAULT_TITLES[commentType] || "Add Comment";

  const canSubmit = useMemo(() => {
    if (!editorValue) return false;
    // strip empty tiptap output (<p></p>) to avoid posting blank comments
    const stripped = String(editorValue).replace(/<[^>]*>/g, "").trim();
    return stripped.length > 0;
  }, [editorValue]);

  const resetEditor = useCallback(() => {
    setEditorValue("");
    setEditorKey((k) => k + 1);
  }, []);

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetEditor();
    onClose?.();
  }, [onClose, resetEditor, submitting]);

  const handleSuccess = useCallback(
    (response) => {
      if (dispatchLoanDrilldownSet && response && !isNil(response?._id)) {
        dispatch(loanDrilldownSet(response));
      }
      if (isFunction(onSuccess)) onSuccess(response);
      resetEditor();
      setSubmitting(false);
      onClose?.();
    },
    [dispatch, dispatchLoanDrilldownSet, onClose, onSuccess, resetEditor],
  );

  const handleFail = useCallback(() => setSubmitting(false), []);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    const htmlString = editorValue;
    setSubmitting(true);

    switch (commentType) {
      case COMMENT_TYPES.EXCEPTION:
        addCommentToUnderwritingException({
          dealId: context.dealId,
          excecptionId: context.exceptionId,
          payload: { htmlString },
          onSuccessFn: handleSuccess,
          onFailFn: handleFail,
        });
        break;
      case COMMENT_TYPES.REQUIREMENT:
        addCommentToUnderwritingRequirement({
          dealId: context.dealId,
          requirementId: context.requirementId,
          commentText: htmlString,
          onSuccessFn: handleSuccess,
          onFailFn: handleFail,
        });
        break;
      case COMMENT_TYPES.TASK:
        createChecklistComment({
          dealId: context.dealId,
          taskListId: context.taskListId,
          taskId: context.taskId,
          data: { htmlString },
          onSuccessFn: handleSuccess,
          onFailFn: handleFail,
        });
        break;
      case COMMENT_TYPES.LOAN:
      default:
        createDealComment({
          loanId: context.dealId,
          data: { htmlString },
          onSuccessFn: handleSuccess,
          onFailFn: handleFail,
        });
        break;
    }
  }, [
    canSubmit,
    editorValue,
    commentType,
    context,
    addCommentToUnderwritingException,
    addCommentToUnderwritingRequirement,
    createChecklistComment,
    createDealComment,
    handleSuccess,
    handleFail,
  ]);

  const loading = submitting || checklistLoading;

  return (
    <BasicModal
      show={open}
      onClose={handleClose}
      title={resolvedTitle}
      maxWidth="sm"
    >
      <Flx column g={2}>
        <Box
          // Stop key events from bubbling to MUI Dialog. Without this the
          // Dialog's FocusTrap can react to Space/Arrow keys and trigger one
          // of the dialog buttons (Cancel / Post Comment), which manifests as
          // "Space does nothing in the editor" while Enter still inserts a
          // newline (Enter is handled by tiptap first).
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
          sx={{
            border: `1px solid ${palette.border}`,
            borderRadius: 1.5,
            background: palette.surface,
            overflow: "hidden",
          }}
        >
          <HtmlEditor
            key={editorKey}
            // Intentionally NOT passing `value` — HtmlEditor's internal sync
            // useEffect calls editor.commands.setContent on every value
            // change, and the HTML serializer trims trailing whitespace on
            // the round-trip, which causes pressed spaces to disappear.
            // Treat the editor as remount-controlled via `key` instead.
            // HtmlEditor emits { htmlString, plainText, editorInstance }.
            // editorInstance has circular tiptap refs (parent ↔ children)
            // that break JSON.stringify on the axios POST — keep just html.
            onChange={(payload) => setEditorValue(payload?.htmlString ?? "")}
            placeholder={placeholder}
            minHeight="140px"
            maxHeight="320px"
            suppressOuterBorder
          />
        </Box>

        <Flx ac jc="flex-end" g={1}>
          <Button
            variant="text"
            onClick={handleClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              color: palette.textMuted,
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            startIcon={<SendRounded sx={{ fontSize: 14 }} />}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {loading ? "Posting…" : "Post Comment"}
          </Button>
        </Flx>

        {Array.isArray(comments) && comments.length > 0 && (
          <Box sx={{ mt: 1, pt: 2, borderTop: `1px solid ${palette.borderSubtle}` }}>
            <Flx ac jb sx={{ mb: 1 }}>
              <Txt
                sx={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: 0.6,
                  color: palette.textFaint,
                  textTransform: "uppercase",
                }}
              >
                Previous Comments{" "}
                <Box
                  component="span"
                  sx={{ color: palette.textFaint, fontWeight: 400, ml: 0.5 }}
                >
                  ({comments.length})
                </Box>
              </Txt>
            </Flx>
            <Flx column g={1}>
              {comments.map((c, i) => (
                <CommentRow
                  key={c?._id || `c-${i}`}
                  comment={c}
                  users={users}
                />
              ))}
            </Flx>
          </Box>
        )}
      </Flx>
    </BasicModal>
  );
};

/* ─── Comment row (read-only display) ──────────────────────────────────────── */

const CommentRow = ({ comment, users }) => {
  // Field shapes vary by comment source:
  //   - Exception/condition/deal comments → postedBy + commentDateTime (per EXCEPTIONS.md §8.1 / §11.3)
  //   - Submitted-via shape              → submittedBy
  //   - Some legacy/derived shapes        → createdBy + createDateTime/createdAt
  const authorEmail =
    comment?.postedBy ||
    comment?.submittedBy ||
    comment?.createdBy ||
    comment?.author;
  const author = authorEmail
    ? getUserFullNameFromEmail(users, authorEmail) || authorEmail
    : "Unknown";
  const createdAt =
    comment?.commentDateTime ||
    comment?.createDateTime ||
    comment?.createdAt;
  const body =
    comment?.htmlString ||
    comment?.commentBody ||
    comment?.body ||
    comment?.message ||
    "";

  return (
    <Flx g={1}>
      <Box sx={{ mt: 0.25 }}>
        <Avatar name={author} />
      </Box>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${palette.border}`,
          background: palette.surface,
          borderRadius: 1.5,
          px: 1.5,
          py: 1,
        }}
      >
        <Flx ac g={1} sx={{ mb: 0.25 }}>
          <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: palette.text }}>
            {author}
          </Txt>
          {createdAt && (
            <Txt sx={{ fontSize: 10.5, color: palette.textFaint }}>
              {formatDate(createdAt)}
            </Txt>
          )}
        </Flx>
        {body ? (
          <Box
            sx={{
              fontSize: 12.5,
              color: palette.textMuted,
              lineHeight: 1.5,
              wordBreak: "break-word",
              overflowWrap: "break-word",
              "& p": { m: 0 },
            }}
            dangerouslySetInnerHTML={{ __html: body }}
          />
        ) : (
          <Txt
            sx={{
              fontSize: 12,
              color: palette.textFaint,
              fontStyle: "italic",
            }}
          >
            <Flx ac g={0.5}>
              <ChatBubbleOutline sx={{ fontSize: 12 }} />
              (no content)
            </Flx>
          </Txt>
        )}
      </Box>
    </Flx>
  );
};

const Avatar = ({ name }) => {
  const initials = useMemo(() => {
    if (!name) return "?";
    return name
      .split(" ")
      .filter(Boolean)
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [name]);

  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: palette.accentLight,
        color: palette.accent,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 10,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {initials}
    </Box>
  );
};

export default CommentModal;
